import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faGraduationCap,
	faUserPlus,
	faSearch,
	faUsers,
	faChevronDown,
	faChevronUp,
	faEdit,
	faMapMarkerAlt,
	faCalendarAlt,
	faEnvelope,
	faPhone,
	faBuilding,
	faUser,
	faSpinner,
	faExclamationTriangle,
	faTrash,
	faEllipsisV,
} from "@fortawesome/free-solid-svg-icons";

import PageContainer from "../../components/layout/PageContainer";
import StudentModal from "./components/StudentModal/StudentModal";
import { Loading } from "../../components/common";

import {
	useGetAllStudentsIncludingInactiveQuery,
	useDeleteStudentMutation,
	useUpdateStudentStatusMutation,
} from "../../store/api/studentApiSlice";
import { useGetDepartmentsQuery } from "../../store/api/departmentApiSlice";
import { useGetAllParentDtosQuery } from "../../store/api/parentApiSlice";

import styles from "./StudentsPage.module.css";

// Actions Dropdown Component
const ActionsDropdown = ({
	student,
	onEdit,
	onDelete,
	isOpen,
	onToggle,
	onClose,
}) => {
	return (
		<div className={styles.actionsDropdown}>
			<button
				className={styles.actionsToggle}
				onClick={onToggle}
				title="More actions"
			>
				<FontAwesomeIcon icon={faEllipsisV} />
			</button>
			{isOpen && (
				<>
					<div className={styles.dropdownOverlay} onClick={onClose} />
					<div className={styles.dropdownMenu}>
						<button
							className={styles.dropdownItem}
							onClick={(e) => {
								onEdit(e);
								onClose();
							}}
						>
							<FontAwesomeIcon icon={faEdit} />
							Edit Student
						</button>
						<button
							className={`${styles.dropdownItem} ${styles.deleteItem}`}
							onClick={(e) => {
								onDelete(e);
								onClose();
							}}
						>
							<FontAwesomeIcon icon={faTrash} />
							Delete Student
						</button>
					</div>
				</>
			)}
		</div>
	);
};

// Status Toggle Component
const StatusToggle = ({ isActive, onToggle, disabled = false }) => {
	return (
		<button
			className={`${styles.statusToggle} ${
				isActive ? styles.active : styles.inactive
			}`}
			onClick={onToggle}
			disabled={disabled}
			title={
				isActive
					? "Active - Click to deactivate"
					: "Inactive - Click to activate"
			}
		>
			<div className={styles.toggleSlider} />
		</button>
	);
};

const StudentsPage = () => {
	// State
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedDepartment, setSelectedDepartment] = useState("");
	const [showInactiveStudents, setShowInactiveStudents] = useState(false);
	const [expandedStudent, setExpandedStudent] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedStudent, setSelectedStudent] = useState(null);
	const [openDropdown, setOpenDropdown] = useState(null);

	// API Hooks
	const {
		data: studentsData = [],
		isLoading: studentsLoading,
		error: studentsError,
	} = useGetAllStudentsIncludingInactiveQuery();

	const { data: departments = [] } = useGetDepartmentsQuery();
	const { data: parents = [] } = useGetAllParentDtosQuery();
	const [deleteStudent] = useDeleteStudentMutation();
	const [updateStudentStatus] = useUpdateStudentStatusMutation();

	// Transform and filter students data
	const filteredStudents = useMemo(() => {
		let filtered = [...studentsData];

		// Filter by search term
		if (searchTerm) {
			filtered = filtered.filter(
				(student) =>
					`${student.firstName} ${student.lastName}`
						.toLowerCase()
						.includes(searchTerm.toLowerCase()) ||
					student.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
					student.lastName.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}

		// Filter by department
		if (selectedDepartment) {
			filtered = filtered.filter(
				(student) => student.departmentId === selectedDepartment
			);
		}

		// Filter by active status
		if (!showInactiveStudents) {
			filtered = filtered.filter((student) => student.isActive);
		}

		return filtered.sort((a, b) =>
			`${a.firstName} ${a.lastName}`.localeCompare(
				`${b.firstName} ${b.lastName}`
			)
		);
	}, [studentsData, searchTerm, selectedDepartment, showInactiveStudents]);

	// Helper functions
	const getDepartmentName = (departmentId) => {
		const department = departments.find((dept) => dept.id === departmentId);
		return department ? department.departmentName : "Unknown Department";
	};

	const getParentInfo = (parentId) => {
		const parent = parents.find((p) => p.id === parentId);
		return parent
			? {
					name: `${parent.firstName} ${parent.lastName}`,
					email: parent.email,
					phone: parent.phoneNumber || "N/A",
			  }
			: { name: "No Parent Assigned", email: "", phone: "" };
	};

	const calculateAge = (dateOfBirth) => {
		if (!dateOfBirth) return "N/A";
		const today = new Date();
		const birthDate = new Date(dateOfBirth);
		let age = today.getFullYear() - birthDate.getFullYear();
		const monthDiff = today.getMonth() - birthDate.getMonth();
		if (
			monthDiff < 0 ||
			(monthDiff === 0 && today.getDate() < birthDate.getDate())
		) {
			age--;
		}
		return age;
	};

	const formatDate = (dateString) => {
		if (!dateString) return "N/A";
		return new Date(dateString).toLocaleDateString();
	};

	// Event handlers
	const handleExpandStudent = (studentId) => {
		setExpandedStudent(expandedStudent === studentId ? null : studentId);
	};

	const handleAddStudent = () => {
		setSelectedStudent(null);
		setIsModalOpen(true);
	};

	const handleEditStudent = (student, e) => {
		e.stopPropagation();
		setSelectedStudent(student);
		setIsModalOpen(true);
	};

	const handleDeleteStudent = async (student, e) => {
		e.stopPropagation();
		if (
			window.confirm(
				`Are you sure you want to delete ${student.firstName} ${student.lastName}?`
			)
		) {
			try {
				await deleteStudent(student.id).unwrap();
			} catch (error) {
				console.error("Failed to delete student:", error);
				alert("Failed to delete student. Please try again.");
			}
		}
	};

	const handleToggleStatus = async (student, e) => {
		e.stopPropagation();
		try {
			await updateStudentStatus({
				id: student.id,
				isActive: !student.isActive,
			}).unwrap();
		} catch (error) {
			console.error("Failed to update student status:", error);
			alert("Failed to update student status. Please try again.");
		}
	};

	const handleDropdownToggle = (studentId, e) => {
		e.stopPropagation();
		setOpenDropdown(openDropdown === studentId ? null : studentId);
	};

	const handleCloseDropdown = () => {
		setOpenDropdown(null);
	};

	// Stats calculation
	const activeStudents = studentsData.filter((s) => s.isActive).length;
	const inactiveStudents = studentsData.filter((s) => !s.isActive).length;

	// Loading state
	if (studentsLoading) {
		return <Loading />;
	}

	// Error state
	if (studentsError) {
		return (
			<PageContainer>
				<div className={styles.errorState}>
					<FontAwesomeIcon icon={faExclamationTriangle} />
					<h2>Error Loading Students</h2>
					<p>Unable to load student data. Please try again.</p>
				</div>
			</PageContainer>
		);
	}

	return (
		<PageContainer>
			<div className={styles.studentsPage}>
				{/* Header */}
				<div className={styles.header}>
					<div className={styles.headerLeft}>
						<div className={styles.title}>
							<h1>
								<FontAwesomeIcon
									icon={faGraduationCap}
									className={styles.titleIcon}
								/>
								Students Management
							</h1>
							<div className={styles.subtitle}>
								Manage student information, departments, and parent assignments
							</div>
						</div>
						<div className={styles.stats}>
							<span className={styles.statPill}>
								<FontAwesomeIcon icon={faUsers} />
								{activeStudents} Active
							</span>
							{inactiveStudents > 0 && (
								<span className={`${styles.statPill} ${styles.inactivePill}`}>
									<FontAwesomeIcon icon={faUsers} />
									{inactiveStudents} Inactive
								</span>
							)}
							<span className={styles.statPill}>
								<FontAwesomeIcon icon={faBuilding} />
								{departments.length} Departments
							</span>
							<span className={styles.statPill}>
								<FontAwesomeIcon icon={faGraduationCap} />
								{filteredStudents.length} Total Showing
							</span>
						</div>
					</div>

					<div className={styles.headerRight}>
						<button className={styles.addBtn} onClick={handleAddStudent}>
							<FontAwesomeIcon icon={faUserPlus} />
							Add Student
						</button>
					</div>
				</div>

				{/* Search and Filters */}
				<div className={styles.searchFilterRow}>
					<div className={styles.searchBar}>
						<FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
						<input
							type="text"
							placeholder="Search students by name..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className={styles.searchInput}
						/>
					</div>

					<div className={styles.filters}>
						<select
							value={selectedDepartment}
							onChange={(e) => setSelectedDepartment(e.target.value)}
							className={styles.filterSelect}
						>
							<option value="">All Departments</option>
							{departments.map((dept) => (
								<option key={dept.id} value={dept.id}>
									{dept.departmentName}
								</option>
							))}
						</select>

						<label className={styles.checkboxLabel}>
							<input
								type="checkbox"
								checked={showInactiveStudents}
								onChange={(e) => setShowInactiveStudents(e.target.checked)}
							/>
							Show Inactive Students
						</label>
					</div>
				</div>

				{/* Students Table */}
				<div className={styles.tableContainer}>
					{filteredStudents.length > 0 ? (
						<table className={styles.table}>
							<thead>
								<tr>
									<th>Student</th>
									<th>Department</th>
									<th>Parent/Guardian</th>
									<th>Status</th>
									<th>Actions</th>
								</tr>
							</thead>
							<tbody>
								{filteredStudents.map((student) => {
									const parentInfo = getParentInfo(student.parentId);
									const isExpanded = expandedStudent === student.id;
									const isDropdownOpen = openDropdown === student.id;

									return (
										<React.Fragment key={student.id}>
											<tr
												className={`${styles.studentRow} ${
													isExpanded ? styles.expanded : ""
												} ${!student.isActive ? styles.inactive : ""}`}
												onClick={() => handleExpandStudent(student.id)}
											>
												<td className={styles.nameCell}>
													<div className={styles.name}>
														<div className={styles.nameContent}>
															<div className={styles.fullName}>
																{student.firstName} {student.lastName}
															</div>
															<div className={styles.studentInfo}>
																Age: {calculateAge(student.dateOfBirth)} •{" "}
																{student.citizenship || "N/A"}
															</div>
														</div>
														<FontAwesomeIcon
															icon={isExpanded ? faChevronUp : faChevronDown}
															className={styles.expandIcon}
														/>
													</div>
												</td>

												<td className={styles.departmentCell}>
													<div className={styles.departmentInfo}>
														<FontAwesomeIcon icon={faBuilding} />
														<span className={styles.departmentName}>
															{getDepartmentName(student.departmentId)}
														</span>
													</div>
												</td>

												<td className={styles.parentCell}>
													<div className={styles.contactInfo}>
														<div className={styles.parentName}>
															<FontAwesomeIcon icon={faUser} />
															<span
																className={styles.parentNameText}
																title={parentInfo.name}
															>
																{parentInfo.name}
															</span>
														</div>
														{parentInfo.email && (
															<div className={styles.emailDisplay}>
																<FontAwesomeIcon icon={faEnvelope} />
																<span
																	className={styles.emailText}
																	title={parentInfo.email}
																>
																	{parentInfo.email}
																</span>
															</div>
														)}
													</div>
												</td>

												<td className={styles.statusCell}>
													<StatusToggle
														isActive={student.isActive}
														onToggle={(e) => handleToggleStatus(student, e)}
													/>
												</td>

												<td className={styles.actionsCell}>
													<ActionsDropdown
														student={student}
														isOpen={isDropdownOpen}
														onToggle={(e) =>
															handleDropdownToggle(student.id, e)
														}
														onClose={handleCloseDropdown}
														onEdit={(e) => handleEditStudent(student, e)}
														onDelete={(e) => handleDeleteStudent(student, e)}
													/>
												</td>
											</tr>

											{/* Expanded Details Row */}
											{isExpanded && (
												<tr className={styles.expandedRow}>
													<td colSpan="5">
														<div className={styles.expandedContent}>
															<div className={styles.expandedGrid}>
																<div className={styles.detailGroup}>
																	<h4>
																		<FontAwesomeIcon icon={faUser} />
																		Personal Information
																	</h4>
																	<div className={styles.detailItem}>
																		<FontAwesomeIcon icon={faCalendarAlt} />
																		<span>
																			Born: {formatDate(student.dateOfBirth)}
																		</span>
																	</div>
																	<div className={styles.detailItem}>
																		<FontAwesomeIcon icon={faMapMarkerAlt} />
																		<span>
																			Place of Birth:{" "}
																			{student.placeOfBirth || "N/A"}
																		</span>
																	</div>
																	<div className={styles.detailItem}>
																		<FontAwesomeIcon icon={faMapMarkerAlt} />
																		<span>
																			Country: {student.country || "N/A"}
																		</span>
																	</div>
																</div>

																<div className={styles.detailGroup}>
																	<h4>
																		<FontAwesomeIcon icon={faUsers} />
																		Parent/Guardian Details
																	</h4>
																	<div className={styles.detailItem}>
																		<FontAwesomeIcon icon={faUser} />
																		<span>{parentInfo.name}</span>
																	</div>
																	{parentInfo.email && (
																		<div className={styles.detailItem}>
																			<FontAwesomeIcon icon={faEnvelope} />
																			<span>{parentInfo.email}</span>
																		</div>
																	)}
																	{parentInfo.phone &&
																		parentInfo.phone !== "N/A" && (
																			<div className={styles.detailItem}>
																				<FontAwesomeIcon icon={faPhone} />
																				<span>{parentInfo.phone}</span>
																			</div>
																		)}
																</div>
															</div>
														</div>
													</td>
												</tr>
											)}
										</React.Fragment>
									);
								})}
							</tbody>
						</table>
					) : (
						<div className={styles.noResults}>
							<FontAwesomeIcon icon={faGraduationCap} />
							<h3>No Students Found</h3>
							<p>
								{searchTerm || selectedDepartment
									? "Try adjusting your search or filter criteria."
									: "Start by adding your first student."}
							</p>
							<button onClick={handleAddStudent} className={styles.addBtn}>
								<FontAwesomeIcon icon={faUserPlus} />
								Add First Student
							</button>
						</div>
					)}
				</div>

				{/* Student Modal */}
				<StudentModal
					student={selectedStudent}
					departments={departments}
					parents={parents}
					isOpen={isModalOpen}
					onClose={() => {
						setIsModalOpen(false);
						setSelectedStudent(null);
					}}
				/>
			</div>
		</PageContainer>
	);
};

export default StudentsPage;
